import { ItineraryItem } from '@src/types/itinerary';
import { DataTable } from 'mantine-datatable';
import 'mantine-datatable/styles.css';
import 'mantine-datatable/styles.layer.css';
import classes from './itinerary-table.module.css';
import { Text, Group, ThemeIcon, Tooltip, ActionIcon, rem, Drawer } from '@mantine/core';
import { IconInfoCircle, IconStarFilled } from '@tabler/icons-react';
import { useMemo, useState } from 'react';
import ItineraryAttractionDetail from '@components/itinerary-attraction-detail';

function ItineraryTable({ itinerary }: { itinerary: ItineraryItem[] }) {
  const [openTitle, setOpenTitle] = useState<string | null>(null);
  // const renderLinkCell = (label?: string, url?: string) => {
  //   if (!label) return null;
  //   if (!url) return label;
  //   return (
  //     <a href={url} target="_blank" rel="noopener noreferrer" aria-label={`${label} (opens in a new tab)`}>
  //       {label} <span aria-hidden="true">↗</span>
  //     </a>
  //   );
  // };

  // Normalize AI string outputs like "null"/"undefined" and empty strings to real null
  const normalizeNullable = (value: unknown) => {
    if (value === null || value === undefined) return null;
    if (typeof value === 'string') {
      const raw = value.trim();
      const lower = raw.toLowerCase();
      if (raw === '' || lower === 'null' || lower === 'undefined') return null;
      return raw;
    }
    return value;
  };

  // Small helper to decorate activity text when an activityId exists
  const ActivityCell = ({
    text,
    id,
    url,
    interest,
  }: {
    text?: string | null;
    id?: string | number | null;
    url?: string | null;
    interest?: number | string | null;
  }) => {
    const safeText = normalizeNullable(text) as string | null;
    if (!safeText) return null;

    const safeId = normalizeNullable(id) as string | number | null;

    const interestVal = normalizeNullable(interest);
    const safeInterest =
      typeof interestVal === 'string'
        ? isNaN(Number(interestVal))
          ? null
          : Number(interestVal)
        : typeof interestVal === 'number'
          ? interestVal
          : null;

    return (
      <Group gap="xs" wrap="nowrap">
        {url ? (
          <a
            href={url ?? undefined}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`${safeText} (opens in a new tab)`}
          >
            {safeText}
          </a>
        ) : (
          <Text>{safeText}</Text>
        )}

        <ActionIcon
          variant="subtle"
          size="sm"
          aria-label={`More info about ${safeText}`}
          onClick={() => setOpenTitle(safeText)}
        >
          <IconInfoCircle />
        </ActionIcon>
        {safeId !== null ? (
          <Tooltip label={`Selected from travel vibe results (Interest: ${safeInterest ?? ''})`} withArrow>
            <ThemeIcon
              size="lg"
              radius="xl"
              variant="light"
              color="yellow"
              aria-label="Selected from travel vibe results"
            >
              <IconStarFilled style={{ width: rem(25), height: rem(25) }} />
              <span
                style={{
                  position: 'absolute',
                  top: '52%',
                  left: '52%',
                  transform: 'translate(-50%, -50%)',
                  fontWeight: 700,
                  lineHeight: 1,
                  fontSize: rem(14),
                  color: 'black',
                  pointerEvents: 'none',
                }}
              >
                {safeInterest ?? ''}
              </span>
            </ThemeIcon>
          </Tooltip>
        ) : null}
      </Group>
    );
  };

  // Optional quick filter
  const [filter] = useState<'all' | 'selected' | 'ai'>('all');
  const records = useMemo(() => {
    if (filter === 'all') return itinerary;
    const isSelected = (r: ItineraryItem) => Boolean(r.amAttractionId || r.pmAttractionId);
    return itinerary.filter((r: ItineraryItem) => (filter === 'selected' ? isSelected(r) : !isSelected(r)));
  }, [itinerary, filter]);

  return (
    <>
      {/*
      TODO: renamed VoyLogic selected
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
        <SegmentedControl
          size="xs"
          value={filter}
          onChange={(v) => setFilter(v as typeof filter)}
          data={[
            { label: 'All', value: 'all' },
            { label: 'Vibe Selected', value: 'selected' },
            { label: 'VoyLogic Suggested', value: 'ai' },
          ]}
          aria-label="Filter activities by selection source"
        />
      </div> */}

      <DataTable
        classNames={{
          root: classes.tableRoot,
          header: classes.tableHeader,
        }}
        withTableBorder
        highlightOnHover
        striped
        borderRadius="sm"
        scrollAreaProps={{ type: 'always', scrollbarSize: 20 }}
        // idAccessor={({ date }) => (date ? `${new Date(date).toISOString()}` : `${Math.random()}`)}
        idAccessor={(record) => `${record.day}-${record.date || 'no-date'}`}
        defaultColumnProps={{
          textAlign: 'right',
          noWrap: false,
        }}
        records={records}
        columns={[
          { accessor: 'day', title: 'Day', noWrap: true, render: (record) => `Day ${record.day}` },
          {
            accessor: 'formattedDate',
            title: 'Date',
            noWrap: true,
            render: (record) => {
              if (!record.date) return null;
              return new Date(record.date)
                .toLocaleDateString('en-GB', {
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric',
                  timeZone: 'UTC',
                })
                .replace(/ /g, '-');
            },
          },
          {
            accessor: 'weekday',
            title: 'Weekday',
            render: (record) => {
              if (!record.date) return null;
              return new Date(record.date).toLocaleDateString('en-US', { weekday: 'long', timeZone: 'UTC' });
            },
          },
          {
            accessor: 'amActivity',
            title: 'AM Activity',
            textAlign: 'left',
            render: (record) => (
              <ActivityCell
                text={record.amActivity}
                id={(record as ItineraryItem).amAttractionId ?? null}
                url={normalizeNullable((record as ItineraryItem).amAttractionUrl) as string | null}
                interest={(record as ItineraryItem).amAttractionInterest ?? null}
              />
            ),
          },
          {
            accessor: 'pmActivity',
            title: 'PM Activity',
            textAlign: 'left',
            render: (record) => (
              <ActivityCell
                text={record.pmActivity}
                id={(record as ItineraryItem).pmAttractionId ?? null}
                url={normalizeNullable((record as ItineraryItem).pmAttractionUrl) as string | null}
                interest={(record as ItineraryItem).pmAttractionInterest ?? null}
              />
            ),
          },
          {
            accessor: 'dinner',
            title: 'Dinner',
            textAlign: 'left',
            // render: (record) => renderLinkCell(record.dinner, (record as ItineraryItem).dinnerUrl),
          },
          {
            accessor: 'accommodations',
            title: 'Accommodations',
            textAlign: 'left',
            // render: (record) => renderLinkCell(record.accommodations, (record as ItineraryItem).accommodationsUrl),
          },
        ]}
      />
      <Drawer
        position="right"
        offset={20}
        radius="md"
        opened={openTitle !== null}
        onClose={() => setOpenTitle(null)}
        padding="md"
        size="30%"
      >
        <ItineraryAttractionDetail activityName={openTitle ?? ''} />
      </Drawer>
    </>
  );
}

export default ItineraryTable;
